import { authorizedViewerUrl } from './viewerAccess';
/**
 * Format DICOM patient name (replace ^ separator with space)
 * DICOM format is typically: LastName^FirstName^MiddleName^Prefix^Suffix
 */
export const formatPatientName = (name) => {
  if (!name) return 'Unknown Patient';
  return name.replace(/\^/g, ' ').trim() || 'Unknown Patient';
};

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

/**
 * Format turnaround time (TAT) in minutes for display.
 * 0 / invalid -> '—', under an hour -> "42m", otherwise -> "1h 22m".
 */
export const formatTatMinutes = (minutes) => {
  const m = Math.round(Number(minutes) || 0);
  if (m <= 0) return '—';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
};

/**
 * DICOM dates (patient birth date, study date) represent a calendar date with
 * no meaningful time/timezone component. Extracting them with local-timezone
 * getters (or toLocaleDateString) can roll the date back a day depending on the
 * viewer's timezone and exactly how the value was stored. This adds a 12-hour
 * buffer before reading via UTC getters to absorb any sub-day storage skew,
 * then reads via UTC so the calendar date itself never shifts — same approach
 * already relied on for the DOB-based secure email password.
 */
export const parseDicomDateParts = (dateValue) => {
  if (!dateValue) return null;

  if (typeof dateValue === 'string' && /^\d{8}$/.test(dateValue)) {
    // Raw DICOM DA value: YYYYMMDD
    return {
      year: parseInt(dateValue.slice(0, 4), 10),
      month: parseInt(dateValue.slice(4, 6), 10),
      day: parseInt(dateValue.slice(6, 8), 10),
    };
  }

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (isNaN(date.getTime())) return null;

  const adjusted = new Date(date.getTime() + 12 * 60 * 60 * 1000);
  return {
    year: adjusted.getUTCFullYear(),
    month: adjusted.getUTCMonth() + 1,
    day: adjusted.getUTCDate(),
  };
};

/**
 * Format DICOM date for display (e.g. "01/15/1980")
 */
export const formatDicomDate = (dateValue) => {
  const parts = parseDicomDateParts(dateValue);
  if (!parts) return 'Unknown';
  return `${String(parts.month).padStart(2, '0')}/${String(parts.day).padStart(2, '0')}/${parts.year}`;
};

/**
 * Format a birth date as YYYYMMDD — used as the DOB-based secure email password.
 */
export const formatDOBPassword = (dateValue) => {
  const parts = parseDicomDateParts(dateValue);
  if (!parts) return null;
  return `${parts.year}${String(parts.month).padStart(2, '0')}${String(parts.day).padStart(2, '0')}`;
};

/**
 * Get modality display name
 */
export const getModalityDisplayName = (modality) => {
  const modalityNames = {
    'CT': 'Computed Tomography',
    'MR': 'Magnetic Resonance',
    'XR': 'X-Ray',
    'US': 'Ultrasound',
    'CR': 'Computed Radiography',
    'DR': 'Digital Radiography',
    'MG': 'Mammography',
    'PT': 'Positron Emission Tomography',
    'NM': 'Nuclear Medicine',
    'OT': 'Other'
  };
  
  return modalityNames[modality] || modality || 'Unknown';
};

/**
 * Get patient age from birth date
 */
export const calculateAge = (birthDate) => {
  const birth = parseDicomDateParts(birthDate);
  if (!birth) return null;

  const today = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth() + 1;
  const todayD = today.getDate();

  let age = todayY - birth.year;
  if (todayM < birth.month || (todayM === birth.month && todayD < birth.day)) {
    age--;
  }

  return age;
};

/**
 * Validate DICOM UID format
 */
export const isValidDicomUID = (uid) => {
  if (!uid) return false;
  
  // DICOM UID should contain only digits and dots, not start/end with dot
  const uidPattern = /^[0-9]+(\.[0-9]+)*$/;
  return uidPattern.test(uid) && uid.length <= 64;
};

/**
 * Generate OHIF viewer URL
 * OHIF viewer runs separately and connects to Orthanc via DICOMweb
 * @param {string} studyInstanceUID - The DICOM Study Instance UID
 * @param {string} ohifUrl - The OHIF viewer base URL (default: localhost:3000)
 */
/** True only for Orthanc's embedded /ohif plugin (not standalone ohif-viewer hostnames). */
export const ohifUsesQueryParamRoute = (ohifBaseUrl) => {
  try {
    const parsed = new URL(String(ohifBaseUrl));
    const path = (parsed.pathname || '/').replace(/\/+$/, '') || '/';
    return path === '/ohif' || path.endsWith('/ohif');
  } catch {
    return /\/ohif\/?$/.test(String(ohifBaseUrl));
  }
};

export const generateOHIFUrl = (studyInstanceUID, ohifUrl = null) => {
  return authorizedViewerUrl(studyInstanceUID, localStorage.getItem('token'), ohifUrl);
};

/**
 * Check if file is potentially a DICOM file
 */
export const isPotentialDicomFile = (filename) => {
  const lower = filename.toLowerCase();
  
  // Skip system files
  if (lower.includes('__macosx') || lower.includes('.ds_store') || 
      lower.includes('thumbs.db') || lower.includes('.git')) {
    return false;
  }
  
  // Check extensions
  const dicomExtensions = ['.dcm', '.dicom', '.dic'];
  if (dicomExtensions.some(ext => lower.endsWith(ext))) {
    return true;
  }
  
  // Check for DICOM-like patterns
  const dicomPatterns = [
    /^\d+$/, // Pure numbers
    /^IM[\d_-]+/i, // IM prefix
    /^CT[\d_-]+/i, // CT prefix  
    /^MR[\d_-]+/i, // MR prefix
    /^US[\d_-]+/i, // US prefix
    /^\d+\.dcm?$/i,
    /^[\d_-]+\.dcm?$/i
  ];
  
  return dicomPatterns.some(pattern => pattern.test(filename)) || filename.length > 4;
};

/**
 * Process DICOM hierarchy from flat image array
 */
export const processImagesIntoHierarchy = (imageList) => {
  const patientMap = new Map();
  
  imageList.forEach(image => {
    const patientKey = image.patientId || 'unknown';
    
    if (!patientMap.has(patientKey)) {
      patientMap.set(patientKey, {
        patientId: image.patientId,
        patientName: image.patientName,
        patientSex: image.patientSex,
        patientBirthDate: image.patientBirthDate,
        studies: new Map()
      });
    }
    
    const patient = patientMap.get(patientKey);
    const studyKey = image.studyInstanceUID || image.orthancStudyId || `study_${image._id}`;
    
    if (!patient.studies.has(studyKey)) {
      patient.studies.set(studyKey, {
        ...image,
        studyInstanceUID: image.studyInstanceUID,
        orthancStudyId: image.orthancStudyId,
        studyDate: image.studyDate,
        studyDescription: image.studyDescription,
        modality: image.modality,
        accessionNumber: image.accessionNumber,
        seriesCount: 0,
        instanceCount: 0,
        series: new Map(),
        reportStatus: image.reportStatus || (image.report ? image.report.status : 'pending')
      });
    }
    
    const study = patient.studies.get(studyKey);
    const seriesKey = image.seriesInstanceUID || image.orthancSeriesId || `series_${image._id}`;
    
    if (!study.series.has(seriesKey)) {
      study.series.set(seriesKey, {
        seriesInstanceUID: image.seriesInstanceUID,
        orthancSeriesId: image.orthancSeriesId,
        seriesDescription: image.seriesDescription,
        modality: image.modality,
        instances: []
      });
      study.seriesCount++;
    }
    
    study.series.get(seriesKey).instances.push(image);
    study.instanceCount++;
  });
  
  // Convert Maps to Arrays
  return Array.from(patientMap.values()).map(patient => ({
    ...patient,
    studies: Array.from(patient.studies.values()).map(study => ({
      ...study,
      series: Array.from(study.series.values())
    }))
  }));
};

/**
 * Get file type from extension
 */
export const getFileType = (filename) => {
  const lower = filename.toLowerCase();
  
  if (lower.endsWith('.zip')) return 'ZIP Archive';
  if (lower.endsWith('.dcm') || lower.endsWith('.dicom')) return 'DICOM';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'JPEG Image';
  if (lower.endsWith('.png')) return 'PNG Image';
  if (lower.endsWith('.tiff') || lower.endsWith('.tif')) return 'TIFF Image';
  
  return 'Unknown';
};

/**
 * Validate patient data
 */
export const validatePatientData = (patientData) => {
  const errors = {};
  
  if (!patientData.patientName) {
    errors.patientName = 'Patient name is required';
  }
  
  if (!patientData.patientId) {
    errors.patientId = 'Patient ID is required';
  }
  
  if (!patientData.patientSex) {
    errors.patientSex = 'Patient sex is required';
  }
  
  if (!patientData.modality) {
    errors.modality = 'Modality is required';
  }
  
  if (!patientData.studyDate) {
    errors.studyDate = 'Study date is required';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Get priority color and icon
 */
export const getPriorityInfo = (priority) => {
  switch (priority) {
    case 'stat':
      return { color: 'error', label: 'STAT (within 1 hr)', urgent: true };
    default:
      return { color: 'error', label: 'STAT (within 1 hr)', urgent: true };
  }
};

/**
 * Sort studies by priority and date
 */
export const sortStudiesByPriority = (studies) => {
  const priorityOrder = { 'stat': 0 };
  
  return studies.sort((a, b) => {
    const aPriority = priorityOrder[a.priority] || 0;
    const bPriority = priorityOrder[b.priority] || 0;
    
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    
    // Sort by date if same priority
    return new Date(b.studyDate || 0) - new Date(a.studyDate || 0);
  });
};

/**
 * Generate unique patient ID
 */
export const generatePatientId = () => {
  return `PAT-${Date.now()}-${Math.random().toString(36).substr(2, 5).toUpperCase()}`;
};

/**
 * Parse DICOM date string
 */
export const parseDicomDate = (dateString) => {
  if (!dateString) return null;
  
  // Handle YYYYMMDD format
  if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
    const year = dateString.substr(0, 4);
    const month = dateString.substr(4, 2);
    const day = dateString.substr(6, 2);
    
    try {
      const date = new Date(`${year}-${month}-${day}`);
      return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
    } catch {
      return null;
    }
  }
  
  // Handle other formats
  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
  } catch {
    return null;
  }
};

/**
 * Get study status info
 */
export const getStudyStatusInfo = (study) => {
  if (!study.report) {
    return { status: 'pending', color: 'warning', label: 'Pending Review' };
  }
  
  switch (study.report.status) {
    case 'completed':
      return { status: 'completed', color: 'success', label: 'Report Complete' };
    case 'in_progress':
      return { status: 'in_progress', color: 'info', label: 'In Progress' };
    case 'pending':
    default:
      return { status: 'pending', color: 'warning', label: 'Pending Review' };
  }
};

// Export all utility functions
const utils = {
  formatPatientName,
  formatFileSize,
  formatTatMinutes,
  formatDicomDate,
  formatDOBPassword,
  parseDicomDateParts,
  getModalityDisplayName,
  calculateAge,
  isValidDicomUID,
  generateOHIFUrl,
  isPotentialDicomFile,
  processImagesIntoHierarchy,
  getFileType,
  validatePatientData,
  getPriorityInfo,
  sortStudiesByPriority,
  generatePatientId,
  parseDicomDate,
  getStudyStatusInfo
};

export default utils;
